import { z } from 'zod';

export const FrequencyPeriodSchema = z.enum(['day', 'week', 'month', 'year']);

const NullableMoney = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.union([z.null(), z.string().regex(/^\d+(\.\d{1,4})?$/, 'Must be a non-negative amount with up to 4 decimal places')]),
);

const NullablePositiveInt = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v === null ? null : Number(v)),
    z.union([z.null(), z.number().int().positive()]),
);

const NullableFrequencyPeriod = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.union([z.null(), FrequencyPeriodSchema]),
);

const NullableAutoIncludeLink = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.union([z.null(), z.literal('eachservice'), z.string().regex(/^[1-9][0-9]*$/, "Must be 'eachservice' or a positive service position")]),
);

const FrequencyPair = z
    .object({
        frequency_limit: NullablePositiveInt,
        frequency_period: NullableFrequencyPeriod,
    })
    .refine((v) => (v.frequency_limit === null) === (v.frequency_period === null), {
        message: 'frequency_limit and frequency_period must be set together',
        path: ['frequency_period'],
    });

interface AutoIncludeFields {
    auto_include: boolean;
    mandatory: boolean;
    auto_include_link: string | null;
}

// Mirrors AddOnService::assertAutoIncludeCoherent() so the client rejects the
// same three contradictions the server does, with the same messages.
function assertAutoIncludeCoherent(v: AutoIncludeFields, ctx: z.RefinementCtx): void {
    if (v.mandatory && !v.auto_include) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'mandatory requires auto_include.', path: ['mandatory'] });
    }
    if (v.auto_include && v.auto_include_link === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'auto_include_link is required when auto_include is set.', path: ['auto_include_link'] });
    }
    if (!v.auto_include && v.auto_include_link !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'auto_include_link must be empty unless auto_include is set.', path: ['auto_include_link'] });
    }
}

export const AssignAddOnSchema = z
    .object({
        addon_id: z.string().uuid(),
        price_override: NullableMoney,
        quantity_limit: NullablePositiveInt,
        auto_include: z.boolean(),
        mandatory: z.boolean(),
        auto_include_link: NullableAutoIncludeLink,
        // Display-only: derived by useVatDerivedCost, stripped from the output below.
        gross_override_display: z.union([z.string(), z.number()]).nullable().optional(),
    })
    .and(FrequencyPair)
    .superRefine(assertAutoIncludeCoherent)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .transform(({ gross_override_display: _grossOverrideDisplayOmit, ...rest }) => rest);

export const UpdateAddOnAssignmentSchema = z
    .object({
        price_override: NullableMoney,
        quantity_limit: NullablePositiveInt,
        auto_include: z.boolean(),
        mandatory: z.boolean(),
        auto_include_link: NullableAutoIncludeLink,
    })
    .and(FrequencyPair)
    .superRefine(assertAutoIncludeCoherent);

export type AssignAddOnInput = z.infer<typeof AssignAddOnSchema>;
export type UpdateAddOnAssignmentInput = z.infer<typeof UpdateAddOnAssignmentSchema>;
